import React, { 
  Fragment, 
  useState, 
  useCallback,
  useRef,
  useEffect,
  useMemo
} from "react";
import { 
  type ItemContentProps,
  type ContentStruct,
} from "./type";
import styles from "./index.module.scss";
import CheckboxDefault from "@/assets/icons/dp-checkbox-default.svg";
import CheckboxChecked from "@/assets/icons/dp-checkbox-checked.svg";
import {
   Checkbox, 
   Button 
} from "@mui/material";
import CDNImage from '@/components/cdnImage/cdnImage';
import DividerAdd from "@/assets/icons/buy-together-divider.svg";
import { 
  JDIPrice
 } from "@/lib/jdiClientUtils";
import { 
  styled as muiStyled 
} from "@mui/material/styles";
import { 
  calculateSkuPrice, 
  addCartByBuyTogether 
} from "@/apis/service";
import UKToast from "@/components/UKToast";
import { getUserUUIDFromCookie } from "@/utils/userUtils";
import { useTranslation } from "@/i18n/client";
import { formatProductLink } from "@/utils/formatProductLink";
import SafeLink from "@/components/SafeLink";
import { useStores } from "@/models";
import { observer } from "mobx-react";
import { sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25217";
import { dataExpTagConfig } from "@/components/product/const";

const StyledButton = muiStyled(Button)(() => ({
  backgroundColor: "#fff",
  borderColor: "#CC0C1C",
  color: "#CC0C1C",
  borderRadius: "4px",
  fontWeight: "bold",
  lineHeight: "36px",
  padding: "0 16px",
  "&:hover": {
    borderColor: "#CC0C1C",
  }
}));

const ProductCard = ({
  imageUrl,
  realPrice, 
  imgStyle, 
  isChecked = true, 
  showCheckbox = true,
  wareName,
  skuId,
  commonTrackParams,
  localInfo,
  onCheckboxChange = () => {} 
}: Omit<ItemContentProps & {
  showCheckbox?: boolean,
  onCheckboxChange?: (checked: boolean) => void,
  isChecked?: boolean, 
  isMainSku?: boolean,
  skuId?: string,
  wareName?: string,
  localInfo?: any,
  imgStyle?: {
    width: number,
    height: number,
  },
  commonTrackParams: any
}, 'isChoose' | 'isMainSku'>) => {
  // const [checked, setChecked] = React.useState(isChecked);
  const renderChild = ()=> {
    return <>
      <div className={styles.productCardImgWrapper}>
        <CDNImage src={imageUrl} width={imgStyle?.width || 90} height={imgStyle?.height || 90} />
      </div>
      {wareName && <div className={styles.displaySkuName}>{wareName}</div>}
      <div className={styles.productCardPriceWrapper}>
        <JDIPrice
          symbol={localInfo?.currencySymbol}
          price={realPrice}
          symbolStyle={{ fontSize: "16px" }}
          integerStyle={{ fontSize: "20px" }}
          decimalPlaceStyle={{ fontSize: "16px" }}
        />
        </div>
    </>
  }
  return <div className={styles.productCard}>
    {
        showCheckbox && <Checkbox
          className={styles.dpBuyCheckbox}
          checked={isChecked}
          onChange={()=> onCheckboxChange(!isChecked)}
          icon={
            <CheckboxDefault />
          }
          checkedIcon={
            <CheckboxChecked />
          }
          sx={{
            color: '#E0E1E5',
            '&.Mui-checked': {
              color: '#1A1A1A',
            }
            
          }}
        />
      }
      {showCheckbox ?  <SafeLink 
        href={formatProductLink(skuId)}
        trackClickParams={{
          event_id: 'Productdetail_FreqBought_Match_Click',
          json_param: {
             ...commonTrackParams,
          },
      }}
      >
        {renderChild()}
      </SafeLink> : <>
        { renderChild() }
      </>}
   
    
  </div>
}
export default observer(function BuyProductList({
  data, 
  localInfo,
  commonTrackParams,
  onHandleAddCart
}: { 
  data: ContentStruct & {
    index?: number,
    i18n: {
      buttonText: string,
      totalPriceText: string
    }
  },
  localInfo: any,
  commonTrackParams: any,
  onHandleAddCart?: ()=> void
}) {
  const { t } = useTranslation();
  const cartUuidRef = useRef<string>('');
  // const [productData, setProductData] = useState<ContentStruct>(data);
  const currentOptId = data?.content
  .map((item) => item.skuId)
  .join("_") + 
  `_${data?.index || 0}`;
  const { dpStore: { 
    changeBuyTogetherState,
    buyTogetherState
  }  } = useStores()
  const initSelectedSkuIds = useMemo<string[]>(() => {
    return data.content?.filter(item=> item.isChoose)
     .map(item=> item.skuId) || []
  }, [data])
  const onCheckboxChanged = useCallback(async (
    checked: boolean,
    index: number, 
    skuId: string, 
    price: string
  ) => {
    const { 
      selectedSkuIds: cacheSelectedSkuIds = initSelectedSkuIds as string[],
      } = buyTogetherState.get(currentOptId) || {}
    const selectedSkuIds = data?.content?.filter(
      item=> {
        if(skuId === item.skuId){
          return checked
        }
        return cacheSelectedSkuIds.includes(item.skuId)
      }
    ).map(item=> item.skuId) || []

    const res =  await calculateSkuPrice({
      skuIds: selectedSkuIds
    })
    if(res?.totalPrice){
      changeBuyTogetherState(currentOptId, selectedSkuIds, res.totalPrice);
    }
    sendClickTrack.Productdetail_FreqBought_Select_Click({
      ...commonTrackParams,
      index: index - 1,
      skuid: skuId,
      price
    })
    
  }, [data]);

  const goToBuy = async () => {
    const { 
      selectedSkuIds: cacheSelectedSkuIds =  initSelectedSkuIds
      } = buyTogetherState.get(currentOptId) || {}
    if (!cacheSelectedSkuIds?.length) {
      return;
    }
    const res = await addCartByBuyTogether({
      cartuuid: cartUuidRef.current,
      products: cacheSelectedSkuIds.map(item => {
        return {
          num: 1,
          skuId: item,
          itemType: 1
        }
      })
    });
    if (res?.code === "200") {
      UKToast.show({
        content: t("dp.successCart"), // 弹窗内容
        icon: "success", // 可选：success, fail, loading, warn, info，影响图标
      });
      window?.JDIheader?.initCartNum();
      onHandleAddCart?.();
    } else {
      UKToast.show({
        content: t("dp.failCart"), // 弹窗内容
        icon: "error", // 可选：success, fail, loading, warn, info，影响图标
      });
    }
    sendClickTrack?.Productdetail_FreqBought_Addtocart_Click({
      ...commonTrackParams,
      skuid: cacheSelectedSkuIds,
      totalAmount: buyTogetherState.get(currentOptId)?.totalPrice || 
        data?.totalPrice
    })

  }
  
  useEffect(() => {
    cartUuidRef.current = getUserUUIDFromCookie();
  }, []);

return <div 
  className={styles.buyBlock}
  >
  <div className={styles.productCardList}
    exptag={"exp|" + EXPOSURE_RECORD.Productdetail_FreqBought_Match_Expo}
    data-exptag-config= { dataExpTagConfig }
    data-exptag-json-param={JSON.stringify({
      ...commonTrackParams,
      ...data?.content.reduce((prev: any, next: ItemContentProps, index: number) =>{
        return next.isMainSku ? prev : {
          ...prev,
          skuid: [...prev.skuid, next.skuId],
          price: [...prev.price, next.realPrice],
          // index: [...prev.index, index - 1]
        }
     }, {
      skuid: [],
      price: [],
      // index: []
     })
    })} 
  
  >
    {
      data?.content.map((item: ItemContentProps, index: number) => {
        const cacheSelectedSkuIds = buyTogetherState.get(currentOptId)?.selectedSkuIds;
        const isChoose = cacheSelectedSkuIds?.includes(item.skuId) ?? item.isChoose;
        return <Fragment 
          key={`buy-product-${index}-${item.skuId}`}>
          <ProductCard
            commonTrackParams={{
              ...commonTrackParams,
              price: item.realPrice,
              skuid: item.skuId,
              index: index - 1,
            }}
            imageUrl={item.imageUrl}
            realPrice={item.realPrice}
            showCheckbox={!item?.isMainSku}
            isChecked={isChoose}
            wareName={item.wareName}
            skuId={item.skuId}
            onCheckboxChange={(checked: boolean)=>{
              onCheckboxChanged(checked, index, item.skuId, item.realPrice);
            }}
            localInfo={localInfo}
          />
          {index !== data.content.length - 1 && <div className={styles.productCardDivider}> 
            <DividerAdd />
          </div>}
        </Fragment>
      })
    }
    </div>
    <div className={styles.bottomAddCartBar}>
      <div className={styles.total}
       exptag={"exp|" + EXPOSURE_RECORD.Productdetail_FreqBought_Total_Expo}
       data-exptag-config= { dataExpTagConfig }
       data-exptag-json-param={JSON.stringify({ 
        ...commonTrackParams,
        totalAmount: buyTogetherState.get(currentOptId)?.totalPrice || 
            data?.totalPrice
      })} 
      >
        <span className={
          styles.label
        }>
           {data?.i18n?.totalPriceText}:
        </span>
        <br />
        <JDIPrice
          symbol={localInfo?.currencySymbol}
          price={
            buyTogetherState.get(currentOptId)?.totalPrice || 
            data?.totalPrice
          }
          symbolStyle={{ fontSize: "16px" }}
          integerStyle={{ fontSize: "24px" }}
          decimalPlaceStyle={{ fontSize: "16px" }}
        />
      </div>
      
      <StyledButton 
        className={styles.addCartBtn}
        exptag={"exp|" + EXPOSURE_RECORD.Productdetail_FreqBought_Addtocart_Expo }
        data-exptag-config= { dataExpTagConfig }
        data-exptag-json-param={JSON.stringify({ 
          ...commonTrackParams,
          skuid: data?.content
            .filter(item=> !item.isMainSku)
            .map(item=> item.skuId),
          totalAmount: buyTogetherState.get(currentOptId)?.totalPrice ||
              data?.totalPrice
        })} 
        variant="outlined"
        onClick={goToBuy}
      >
        {data?.i18n?.buttonText}
      </StyledButton>
    </div>
</div>
})